import * as XLSX from 'xlsx'
import { saveAs } from 'file-saver'
import { IChatManagementReport, IIntentRow } from './models'
import { FINDING_LABELS, formatHour, formatLocalDate, formatLocalDay, successTreatmentLabel } from './viewModel'

/**
 * İstemci tarafı Excel (spec §8.4). SheetJS CE hücre rengi ve grafik yazmaz; sayılar gerçek sayı,
 * oranlar 0–1 kesir + "0.0%" biçimiyle yazılır. dataHelper içe aktarılmaz (jsdom testlerini kırar).
 */
export type SheetCell = string | number | null | { value: number; format: string }

export interface SheetSpec {
  name: string
  rows: SheetCell[][]
  columnWidths: number[]
}

/** Yerel gün, yyyy-MM-dd. */
export interface ReportPeriod {
  start: string
  end: string
}

export const SHEET_NAMES = ['Yönetim Raporu', 'Yanıt Türleri'] as const

const percent = (value: number): SheetCell => ({ value: value / 100, format: '0.0%' })
const count = (value: number): SheetCell => ({ value, format: '#,##0' })
const decimal = (value: number): SheetCell => ({ value, format: '0.0' })
/** Oran hücresi: satırın paydası (soru) 0 ise "0%" yerine boş hücre yazılır (spec §4.3); satırın kendisi kalır. */
const rateOf = (value: number, total: number): SheetCell => (total === 0 ? null : percent(value))

const buildManagementSheet = (report: IChatManagementReport, period: ReportPeriod): SheetSpec => {
  const kpis = report.Kpis
  const performance = report.IntentPerformance
  const rows: SheetCell[][] = []
  const blank = () => rows.push([])
  const intentRow = (code: string, label: string, row: IIntentRow): SheetCell[] => [
    code,
    label,
    count(row.Total),
    count(row.SuccessCounted),
    count(row.NoResult),
    rateOf(row.SuccessRate, row.Total),
    rateOf(row.NoResultRate, row.Total),
    count(row.Failed),
  ]

  rows.push(['B-Gini AI Asistan – Yönetim Raporu'])
  rows.push([
    'Rapor dönemi:',
    formatLocalDate(period.start),
    formatLocalDate(period.end),
    `Saat dilimi: ${report.TimeZone}`,
  ])
  blank()
  rows.push([
    'Toplam Soru',
    'Aktif Bayi',
    'Günlük Ort. Soru',
    'Başarı Oranı',
    'Tam Başarı',
    'Veri Bulunamadı',
    'Anlamadı (Fallback)',
    'Olumlu Oy',
    'Olumsuz Oy',
    'Oy Verme Oranı',
  ])
  rows.push([
    count(kpis.TotalTurns),
    count(kpis.ActiveDealers),
    decimal(kpis.AverageTurnsPerActiveDay),
    percent(kpis.SuccessRate),
    percent(kpis.FullSuccessRate),
    percent(kpis.NoResultRate),
    percent(kpis.FallbackRate),
    count(kpis.PositiveVotes),
    count(kpis.NegativeVotes),
    percent(kpis.VoteRate),
  ])
  rows.push([
    'tur',
    'benzersiz bayi kodu',
    'soru / aktif gün',
    'yönetim tanımı',
    'success / değerlendirilen',
    'noResult / değerlendirilen',
    'fallback / değerlendirilen',
    null,
    null,
    'oy / değerlendirilen',
  ])
  blank()

  rows.push(['1. Yanıt Türü Dağılımı'])
  rows.push(['Yanıt Türü', 'Anlamı', 'Değerlendirme', 'Adet', 'Pay', 'Kullanıcı ne görür', 'Başarı Sayılır?'])
  report.Outcomes.forEach(row =>
    rows.push([
      row.Code,
      row.Label,
      row.Category,
      count(row.Count),
      percent(row.Share),
      row.UserSees,
      successTreatmentLabel(row.SuccessTreatment),
    ])
  )
  rows.push(['Toplam', null, null, count(kpis.TotalTurns), percent(100), null, count(kpis.SuccessCountedTurns)])
  blank()

  rows.push(['2. Performans Kategorileri'])
  rows.push(['Kategori', 'Açıklama', 'Adet', 'Pay', 'Başarı Sayılır?'])
  report.Categories.forEach(row =>
    rows.push([
      row.Label,
      row.Description,
      count(row.Count),
      percent(row.Share),
      successTreatmentLabel(row.SuccessTreatment),
    ])
  )
  rows.push(['Toplam', null, count(kpis.TotalTurns), percent(100), percent(kpis.SuccessRate)])
  blank()

  rows.push(['3. Günlük Trend'])
  rows.push(['Gün', 'Soru', 'Başarılı (sayılan)', 'Başarı %', 'Veri Yok %', 'Fallback %'])
  report.Daily.forEach(day =>
    rows.push([
      formatLocalDay(day.Date),
      count(day.Total),
      count(day.SuccessCounted),
      rateOf(day.SuccessRate, day.Total),
      rateOf(day.NoResultRate, day.Total),
      rateOf(day.FallbackRate, day.Total),
    ])
  )
  rows.push([
    'Toplam',
    count(kpis.TotalTurns),
    count(kpis.SuccessCountedTurns),
    percent(kpis.SuccessRate),
    percent(kpis.NoResultRate),
    percent(kpis.FallbackRate),
  ])
  blank()

  rows.push(['4. Saatlik Yoğunluk'])
  rows.push(['Saat', 'Soru', 'Pay', 'Başarı %'])
  report.Hourly.forEach(hour =>
    rows.push([formatHour(hour.Hour), count(hour.Total), percent(hour.Share), rateOf(hour.SuccessRate, hour.Total)])
  )
  rows.push(['Toplam', count(kpis.TotalTurns), percent(100), percent(kpis.SuccessRate)])
  blank()

  rows.push(['5. Araç (Niyet) Bazında Performans'])
  rows.push(['Araç', 'İşlev', 'Soru', 'Başarılı (sayılan)', 'Veri Yok', 'Başarı %', 'Veri Yok %', 'Başarısız'])
  performance.Top.forEach(row => rows.push(intentRow(row.Intent, row.Label, row)))
  if (performance.Other.Total > 0)
    rows.push(intentRow('Diğer araçlar', `Diğer ${performance.OtherIntentCount} araç`, performance.Other))
  rows.push(intentRow('Toplam', '', performance.Totals))
  blank()

  rows.push(['6. Soru Giriş Kanalı'])
  rows.push(['Kanal', 'Soru', 'Pay', 'Başarı %', 'Veri Yok %', 'Fallback %'])
  report.Channels.forEach(row =>
    rows.push([
      row.Label,
      count(row.Total),
      percent(row.Share),
      rateOf(row.SuccessRate, row.Total),
      rateOf(row.NoResultRate, row.Total),
      rateOf(row.FallbackRate, row.Total),
    ])
  )
  rows.push([
    'Toplam',
    count(kpis.TotalTurns),
    percent(100),
    percent(kpis.SuccessRate),
    percent(kpis.NoResultRate),
    percent(kpis.FallbackRate),
  ])
  blank()

  rows.push(['7. Öne Çıkan Bulgular ve Öneriler'])
  rows.push(['Tür', 'Bulgu'])
  report.Findings.forEach(finding => rows.push([FINDING_LABELS[finding.Kind], finding.Text]))

  return { name: SHEET_NAMES[0], rows, columnWidths: [26, 28, 22, 14, 14, 26, 18, 12, 12, 16] }
}

const buildDefinitionsSheet = (report: IChatManagementReport): SheetSpec => {
  const rows: SheetCell[][] = [
    ['B-Gini – Yanıt Türü Tanımları'],
    [],
    ['Kod', 'Anlamı', 'Atanma kuralı', 'Kullanıcı ne görür', 'Yorum', 'Adet', 'Pay', 'Başarı Sayılır?'],
  ]
  report.Outcomes.forEach(row =>
    rows.push([
      row.Code,
      row.Label,
      row.AssignmentRule,
      row.UserSees,
      row.Interpretation,
      count(row.Count),
      percent(row.Share),
      successTreatmentLabel(row.SuccessTreatment),
    ])
  )
  rows.push([], ['Performans Kategorileri'], ['Kategori', 'Açıklama', 'Yanıt türleri', 'Başarı Sayılır?'])
  report.Categories.forEach(row =>
    rows.push([row.Label, row.Description, row.Outcomes.join(', '), successTreatmentLabel(row.SuccessTreatment)])
  )

  return { name: SHEET_NAMES[1], rows, columnWidths: [18, 22, 40, 34, 44, 10, 10, 16] }
}

export const buildManagementReportSheets = (report: IChatManagementReport, period: ReportPeriod): SheetSpec[] => [
  buildManagementSheet(report, period),
  buildDefinitionsSheet(report),
]

export const toWorkbook = (sheets: SheetSpec[]): XLSX.WorkBook => {
  const book = XLSX.utils.book_new()

  sheets.forEach(spec => {
    const values = spec.rows.map(row =>
      row.map(cell => (cell !== null && typeof cell === 'object' ? cell.value : cell))
    )
    const sheet = XLSX.utils.aoa_to_sheet(values)

    spec.rows.forEach((row, rowIndex) =>
      row.forEach((cell, columnIndex) => {
        if (cell === null || typeof cell !== 'object') return
        const target = sheet[XLSX.utils.encode_cell({ r: rowIndex, c: columnIndex })] as XLSX.CellObject | undefined
        if (target) target.z = cell.format
      })
    )

    sheet['!cols'] = spec.columnWidths.map(wch => ({ wch }))
    XLSX.utils.book_append_sheet(book, sheet, spec.name)
  })

  return book
}

export const managementReportFileName = (period: ReportPeriod): string =>
  `chatbot-yonetim-raporu_${period.start}_${period.end}.xlsx`

export const exportManagementReport = (report: IChatManagementReport, period: ReportPeriod): void => {
  const book = toWorkbook(buildManagementReportSheets(report, period))
  const buffer = XLSX.write(book, { bookType: 'xlsx', type: 'array' })
  saveAs(new Blob([buffer], { type: 'application/octet-stream' }), managementReportFileName(period))
}
